/* -----------------Globals--------------- */
import { Dialog } from "react-native-paper";

/* -----------------Types--------------- */
import type { FeedbackParams } from "../types";

/* -----------------Child components--------------- */
import { FeedbackContent } from "./FeedbackContent";

import DialogWrapper from "@memorang/ui/src/components/DialogWrapper";
import { useToast } from "@memorang/ui/src/components/Toast";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { HandleTracking } from "../../types";
import { events } from "../constants";

interface Props {
	title?: string;
	subtitle?: string;
	visible: boolean;
	onDismiss: () => void;
	handleTracking: HandleTracking;
	feedbackDialogParams: FeedbackParams;
	subtitleSuffix?: string;
	feedbackPlaceholder?: string;
	icon?: string;
	cta?: string;
	handlePostFeedback: (feedback: string, email: string) => void;
}

const FeedbackDialog = ({
	title,
	subtitle,
	visible,
	onDismiss,
	handleTracking,
	feedbackDialogParams,
	subtitleSuffix,
	feedbackPlaceholder,
	icon,
	cta,
	handlePostFeedback,
}: Props) => {
	const { showToast } = useToast();
	const { t } = useTranslation();

	useEffect(() => {
		if (visible) {
			handleTracking(events.dialogOpened);
		}
	}, [handleTracking, visible]);

	const handleCloseDialog = () => {
		handleTracking(events.dialogClosed);
		onDismiss();
	};

	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleCloseDialog}
			maxWidth="xs"
		>
			{icon && <Dialog.Icon icon={icon} />}
			<Dialog.Title
				style={{
					textAlign: "center",
				}}
			>
				{title || t("dialog.feedback.title")}
			</Dialog.Title>
			<Dialog.Content>
				<FeedbackContent
					feedbackDialogParams={feedbackDialogParams}
					handlePostFeedback={handlePostFeedback}
					onDismiss={handleCloseDialog}
					subtitle={subtitle}
					subtitleSuffix={subtitleSuffix}
					feedbackPlaceholder={feedbackPlaceholder}
					showFeedbackConfirmationToast={() =>
						showToast({
							message: "Feedback submitted. Thank you!",
						})
					}
					cta={cta}
				/>
			</Dialog.Content>
		</DialogWrapper>
	);
};

export { FeedbackDialog };
